import React, { useState } from 'react';
import { X, MessageSquare, Mail, Copy, Check, Share2 } from 'lucide-react';

/**
 * Share sheet with concrete targets — used as a fallback when the browser
 * doesn't support the native Web Share API (desktop Chrome, etc.), and
 * as an alternative UI when the caller wants explicit options.
 *
 * Targets: WhatsApp, Messages/SMS, Email, X/Twitter, Facebook, Copy.
 * All open in a new tab so the caller flow isn't interrupted.
 *
 * Props:
 *   open    boolean
 *   onClose ()=>void
 *   title   modal title
 *   text    the message body (should end with the URL for maximum copy-safety)
 *   url     the shareable URL — included in target intents where needed
 */
export default function ShareSheet({ open, onClose, title = 'Share', text, url }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const fullMessage = text?.includes(url) ? text : `${text || ''} ${url || ''}`.trim();
  const enc = encodeURIComponent;

  const targets = [
    {
      id: 'whatsapp', label: 'WhatsApp',
      href: `https://wa.me/?text=${enc(fullMessage)}`,
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
          <path d="M20.5 3.5A11.9 11.9 0 0 0 12 0C5.4 0 .1 5.3.1 11.9c0 2.1.6 4.1 1.6 5.9L0 24l6.4-1.7a11.9 11.9 0 0 0 5.6 1.4h.1c6.6 0 11.9-5.3 11.9-11.9a11.8 11.8 0 0 0-3.5-8.3ZM12 21.5h-.1a9.6 9.6 0 0 1-4.9-1.3l-.4-.2-3.7 1 1-3.7-.2-.4A9.6 9.6 0 0 1 2.5 12c0-5.3 4.3-9.5 9.5-9.5S21.5 6.8 21.5 12 17.2 21.5 12 21.5Zm5.4-7.1c-.3-.1-1.8-.9-2-1s-.5-.1-.7.1-.8 1-.9 1.2-.3.2-.6.1a7.7 7.7 0 0 1-2.3-1.4 8.4 8.4 0 0 1-1.6-2c-.2-.3 0-.4.1-.6l.4-.5.2-.4a.5.5 0 0 0 0-.4c0-.1-.6-1.5-.9-2-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 2.9 2.9 0 0 0-.9 2.1 5 5 0 0 0 1 2.6 11.5 11.5 0 0 0 4.4 3.9c.6.3 1.1.4 1.4.5.6.2 1.1.2 1.6.1a2.7 2.7 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .1-1.2c-.1-.1-.3-.2-.6-.3Z"/>
        </svg>
      ),
    },
    {
      id: 'sms',  label: 'Messages',
      href: `sms:?body=${enc(fullMessage)}`,
      icon: <MessageSquare className="w-5 h-5" />,
    },
    {
      id: 'email', label: 'Email',
      href: `mailto:?subject=${enc(title)}&body=${enc(fullMessage)}`,
      icon: <Mail className="w-5 h-5" />,
    },
    {
      id: 'twitter', label: 'X / Twitter',
      href: `https://twitter.com/intent/tweet?text=${enc(fullMessage)}`,
      icon: (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M18.9 2H22l-7.4 8.4L23.2 22h-6.9l-5.4-7-6.2 7H1.6l7.9-9L1.1 2h7l4.9 6.5L18.9 2Zm-2.4 18h1.9L7.6 4H5.6l10.9 16Z"/>
        </svg>
      ),
    },
    {
      id: 'facebook', label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text || '')}`,
      icon: (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
          <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-3h2.4V9.4c0-2.4 1.5-3.8 3.7-3.8 1 0 2.1.2 2.1.2v2.4h-1.2c-1.2 0-1.6.7-1.6 1.5V12H16l-.4 3h-2.2v7A10 10 0 0 0 22 12Z"/>
        </svg>
      ),
    },
  ];

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* ignore */}
  };

  const tryNative = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        onClose();
      }
    } catch {/* dismissed */}
  };

  return (
    <div
      className="fixed inset-0 z-[10050] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
      onClick={onClose}
      data-testid="share-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-5 text-white"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[#BEFF38] text-[11px] uppercase tracking-wider font-black">Share</p>
            <h3 className="text-lg font-bold mt-0.5">{title}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="share-sheet-close" className="h-8 w-8 rounded-full grid place-items-center bg-zinc-800 hover:bg-zinc-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3 mb-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-black">Message</p>
          <p className="text-sm text-zinc-200 mt-1 leading-snug">{text}</p>
          {url && <p className="text-[11px] text-[#BEFF38] mt-1 break-all">{url}</p>}
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {targets.map((t) => (
            <a
              key={t.id}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-target-${t.id}`}
              className="flex flex-col items-center justify-center gap-1.5 h-20 rounded-2xl border border-zinc-800 bg-zinc-950 hover:border-[#BEFF38]/40 hover:bg-zinc-900 transition-colors text-zinc-200"
            >
              {t.icon}
              <span className="text-[10px] font-semibold text-zinc-300">{t.label}</span>
            </a>
          ))}
          <button
            type="button"
            onClick={doCopy}
            data-testid="share-target-copy"
            className={`flex flex-col items-center justify-center gap-1.5 h-20 rounded-2xl border transition-colors ${
              copied
                ? 'border-[#BEFF38] bg-[#BEFF38]/10 text-[#BEFF38]'
                : 'border-zinc-800 bg-zinc-950 hover:border-[#BEFF38]/40 hover:bg-zinc-900 text-zinc-200'
            }`}
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            <span className="text-[10px] font-semibold">{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>

        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            type="button"
            onClick={tryNative}
            data-testid="share-native"
            className="mt-4 w-full h-11 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            More share options…
          </button>
        )}
      </div>
    </div>
  );
}
